export type AlignModes =
  | "TopLeft"
  | "Top"
  | "TopRight"
  | "RightTop"
  | "Right"
  | "RightBottom"
  | "BottomRight"
  | "Bottom"
  | "BottomLeft"
  | "LeftBottom"
  | "Left"
  | "LeftTop"
  | "Center";

export const AlignOrigin = {
  Top: "origin-bottom",
  TopLeft: "origin-bottom-left",
  TopRight: "origin-bottom-right",
  Right: "origin-left",
  RightTop: "origin-top-left",
  RightBottom: "origin-bottom-left",
  Bottom: "origin-top",
  BottomRight: "origin-top-right",
  BottomLeft: "origin-top-left",
  Left: "origin-right",
  LeftBottom: "origin-bottom-right",
  LeftTop: "origin-top-right",
};

interface ActionOptions {
  reference: { getBoundingClientRect(): DOMRect };
  align: AlignModes;
}

export function place(node: HTMLElement, options: ActionOptions) {
  let _options = options;

  const observer = new MutationObserver(() => placeNode(node, _options));
  observer.observe(node, {
    childList: true,
    subtree: true,
    attributes: true,
  });

  placeNode(node, options);

  return {
    update: (options: ActionOptions) => {
      _options = options;
      placeNode(node, options);
    },
  };
}

function placeNode(node: HTMLElement, options: ActionOptions) {
  const { reference, align } = options;

  if (!reference) return;

  let rect = reference.getBoundingClientRect();
  const { width, height } = node.getBoundingClientRect();

  // clamp to viewport
  const e = document.documentElement;
  const viewTop = e.scrollTop;
  const viewLeft = e.scrollLeft;
  const viewBottom = viewTop + window.innerHeight;
  const viewRight = viewLeft + window.innerWidth;

  // turn bounds into document coordinates
  let bounds = {
    top: rect.top + e.scrollTop,
    left: rect.left + e.scrollLeft,
    right: rect.right + e.scrollLeft,
    bottom: rect.bottom + e.scrollTop,
    width: rect.width,
    height: rect.height,
  };

  const clampTop = (value) => Math.max(Math.min(value, viewBottom - height), viewTop) + "px";
  const clampLeft = (value) => Math.max(Math.min(value, viewRight - width), viewLeft) + "px";

  switch (align) {
    case "TopLeft":
      node.style.top = clampTop(bounds.top - height);
      node.style.left = clampLeft(bounds.left);
      break;
    case "Top":
      node.style.top = clampTop(bounds.top - height);
      node.style.left = clampLeft(bounds.left + bounds.width / 2 - width / 2);
      break;
    case "TopRight":
      node.style.top = clampTop(bounds.top - height);
      node.style.left = clampLeft(bounds.right - width);
      break;
    case "RightTop":
      node.style.top = clampTop(bounds.top);
      node.style.left = clampLeft(bounds.right);
      break;
    case "Right":
      node.style.top = clampTop(bounds.top + bounds.height / 2 - height / 2);
      node.style.left = clampLeft(bounds.right);
      break;
    case "RightBottom":
      node.style.top = clampTop(bounds.bottom - height);
      node.style.left = clampLeft(bounds.right);
      break;
    case "Bottom":
      node.style.top = clampTop(bounds.bottom);
      node.style.left = clampLeft(bounds.left + bounds.width / 2 - width / 2);
      break;
    case "BottomRight":
      node.style.top = clampTop(bounds.bottom);
      node.style.left = clampLeft(bounds.right - width);
      break;
    case "BottomLeft":
      node.style.top = clampTop(bounds.bottom);
      node.style.left = clampLeft(bounds.left);
      break;
    case "Left":
      node.style.top = clampTop(bounds.top + bounds.height / 2 - height / 2);
      node.style.left = clampLeft(bounds.left - width);
      break;
    case "LeftBottom":
      node.style.top = clampTop(bounds.bottom - height);
      node.style.left = clampLeft(bounds.left - width);
      break;
    case "LeftTop":
      node.style.top = clampTop(bounds.top);
      node.style.left = clampLeft(bounds.left - width);
      break;
    case "Center":
      node.style.top = clampTop(bounds.top + bounds.height / 2 - height / 2);
      node.style.left = clampLeft(bounds.left + bounds.width / 2 - width / 2);
      break;
  }
}
